'use client'

import type { AitypesInsight } from '@polaris/api-client/client-next'
import { featureFlagsAtom } from '@polaris/core/stores'
import { ClientOnly } from '@quirks/react'
import { useAtomValue, useSetAtom } from 'jotai'
import Link from 'next/link'
import { Button } from '@/src/components/ui/button'
import { useHasAccounts } from '@/src/hooks/account/use-has-accounts'
import type { User } from '@/src/services/user'
import {
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'
import { PortfolioButton } from '../portfolio-drawer/portfolio-button'
import TokenImg from '../ui/token-img'

interface AuthSectionProps {
  showBackButton: boolean
  isRoutePage: boolean
  isInsightPage: boolean
  /**
   * Server-side computed values to prevent hydration mismatch.
   * These should match what will be computed on the client after hydration.
   */
  serverUser: User
  insight?: AitypesInsight
}

const TradeButton = ({ insight }: { insight?: AitypesInsight }) => (
  <Button
    variant='primary'
    size={{
      default: 'sm',
      sm: 'md',
    }}
    asChild
  >
    <Link
      href={
        insight?.subjectSelector && insight?.subjectValue?.ticker
          ? `/tokens?ticker=${insight.subjectValue?.ticker}`
          : '/tokens?ticker=OSMO'
      }
      prefetch
    >
      {insight?.subjectSelector && insight?.subjectValue?.logoImgURL ? (
        <TokenImg
          src={insight.subjectValue?.logoImgURL}
          width={24}
          height={24}
          className='shrink-0'
          unoptimized
        />
      ) : null}
      Trade
    </Link>
  </Button>
)

const LoginButton = () => {
  const setOnboardingDialog = useSetAtom(onboardingDialogAtom)

  return (
    <Button
      variant='secondary'
      size={{
        default: 'sm',
        sm: 'md',
      }}
      onClick={() => {
        setOnboardingDialog(openOnboardingDialog('login'))
      }}
      data-testid='login-button'
      className='hidden md:inline-flex'
    >
      Login
    </Button>
  )
}

/**
 * Authentication section of the navbar that handles login/signup buttons
 * and portfolio button. Uses server-computed values to prevent flashing.
 */
export function AuthSection({
  showBackButton,
  isRoutePage,
  isInsightPage,
  serverUser,
  insight,
}: AuthSectionProps) {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const hasAccounts = useHasAccounts()

  const TradeBtn =
    (!showBackButton && !isRoutePage) || isInsightPage ? (
      <TradeButton insight={insight} />
    ) : null

  return (
    <ClientOnly>
      {TradeBtn}
      {featureFlags.newLoginFlow &&
      serverUser.id === 'guest' &&
      !hasAccounts ? (
        <LoginButton />
      ) : (
        <PortfolioButton user={serverUser} />
      )}
    </ClientOnly>
  )
}
